import MarkdownPreview from "@uiw/react-markdown-preview";
import { DateTime } from "luxon";
import { useRef } from "react";
import { Fragment } from "react/jsx-runtime";
import type { ChatAttachment } from "../../../api/quotation";
import type { SubmitQuotationRequestRQ } from "../../../api/quotation/types";
import { NotificationBanner } from "../../banners/NotificationBanner";
import { Loader } from "../../widgets/Loader";
import { AgentMessage } from "./chat/AgentMessage";
import { useSubagentOptionsForQuotation } from "./hooks/subagents";
import { ExtraCommentsSection } from "./QuotationRequestExtraComments";

interface Message {
  chat_session_uid: string;
  id?: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

type QuotationRequestSummaryProps = {
  chatSessionUid: string;
  displayOnly: boolean;
  sourceEntity: string;
  targetEntity: string;
  message: Message;
  isAgentThinking: boolean;
  isSubmitting: boolean;
  summaryFormRef: React.RefObject<HTMLFormElement>;
  attachmentsList: ChatAttachment[];
  onGenerateQuotation: (payload: SubmitQuotationRequestRQ) => void;
};

export function QuotationRequestSummary({
  displayOnly = false,
  chatSessionUid,
  sourceEntity,
  targetEntity,
  message,
  isAgentThinking,
  isSubmitting,
  summaryFormRef,
  attachmentsList,
  onGenerateQuotation,
}: QuotationRequestSummaryProps) {
  const userIsSubagent = sourceEntity.includes("___subagent");

  const { subagentOptions, isFetching: isSubagentsFetching } =
    useSubagentOptionsForQuotation(sourceEntity);

  subagentOptions.unshift({
    label: "------",
    value: "",
    name: "",
    email: "",
    legal_title: "",
  });

  const loading = isSubagentsFetching;
  const extraCommentsRef = useRef<HTMLTextAreaElement>(null);

  return loading ? (
    <Loader />
  ) : isSubmitting ? (
    <Fragment>
      <div />
      <div className="grid gap-3 place-content-center place-items-center h-full place-self-center">
        <Loader />
        <span className="text-center z-[9999] mt-30 text-lg">
          Please wait while we are submitting your quotation request.
        </span>
        <span className="text-center z-[9999] text-lg">Please don't close this window.</span>
      </div>
    </Fragment>
  ) : (
    <div className="grid gap-3 overflow-hidden">
      {!displayOnly && (
        <NotificationBanner
          variant="warning"
          title="Please make sure to review the quotation request summary before proceeding with generating the quotation request."
          description={
            <p>
              <ul>
                <li className="list-disc">
                  If you wish to make changes, please use the <strong>Back button</strong> to go
                  back to the chat and ask the agent to make the necessary changes.
                </li>
                <li className="list-disc">
                  To proceed, please make sure to provide a <strong>title</strong> for the quotation
                  request and <strong>optionally select a sub-agent</strong>.
                </li>
              </ul>
            </p>
          }
        />
      )}
      <div className="grid gap-3 grid-rows-[auto_auto_1fr] overflow-hidden p-3">
        <h6>Quotation Request Summary</h6>
        {!displayOnly && (
          <form
            ref={summaryFormRef}
            action=""
            className="grid gap-3 p-3 bg-gray-100 rounded-md grid-cols-[1fr_max-content_max-content] w-full relative items-end"
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              const form = new FormData(e.target as HTMLFormElement);
              const title = form.get("title") as string;

              let subAgentEntity = "";
              if (!userIsSubagent) {
                subAgentEntity = form.get("sub-agent") as string;
              } else {
                subAgentEntity = sourceEntity;
              }

              const extraComments = extraCommentsRef.current?.value || "";
              const dueDate = form.get("due_date") as string;

              const payload: SubmitQuotationRequestRQ = {
                session_id: "",
                chat_session_uid: chatSessionUid,
                extra_comments: extraComments,
                quotation_title: title,
                source_entity: subAgentEntity,
                target_entity: targetEntity,
                due_date: dueDate,
              };

              onGenerateQuotation(payload);
            }}
          >
            <div className="grid gap-1">
              <label htmlFor="title" className="font-bold">
                Quotation Request Title <small>(10 characters minimum)</small>
              </label>
              <input
                type="text"
                name="title"
                className="p-2 border-gray-200 rounded-md bg-white"
                required
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="title" className="font-bold">
                Offer Due Date
              </label>
              <input
                type="date"
                name="due_date"
                className="p-2 border-gray-200 rounded-md bg-white"
                required
                defaultValue={DateTime.now().plus({ days: 2 }).toISODate()}
              />
            </div>
            {!userIsSubagent && (
              <div className="grid gap-1 relative">
                <label htmlFor="title" className="font-bold">
                  Sub-agent <small>(optional)</small>
                </label>
                <select
                  name="sub-agent"
                  className="p-2 border-gray-200 rounded-md bg-white max-w-[30vw]"
                >
                  {subagentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </form>
        )}
        <div className="overflow-hidden grid grid-cols-[2fr_2fr_1fr] gap-3 pb-3">
          <div className="overflow-auto bg-gray-100 rounded-md p-3">
            {attachmentsList.map((attachment, idx) => {
              return (
                <div key={attachment.uid}>
                  <div className="bg-gray-200 rounded-md p-2">
                    <span className="text-black">
                      {idx + 1}. {attachment.filename}
                    </span>
                  </div>
                  <MarkdownPreview
                    source={attachmentsList
                      .map((attachment) => attachment.parsed_data)
                      .join("\n\n")}
                    className="bg-gray-100! p-3"
                    wrapperElement={{
                      "data-color-mode": "light",
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className="overflow-auto">
            <AgentMessage message={message} isGlobalLoading={isAgentThinking} />
          </div>
          <ExtraCommentsSection ref={extraCommentsRef} className="h-full" />
        </div>
      </div>
    </div>
  );
}
